import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar, ClipboardList, DollarSign, BookOpen, TrendingUp, 
  CheckCircle, XCircle, Clock, GraduationCap, Bell, CreditCard
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [studentInfo, setStudentInfo] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [fees, setFees] = useState([]);
  const [exams, setExams] = useState([]);
  const [grades, setGrades] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch student info first
      const studentRes = await axios.get(`${API_URL}/api/students/me`, { headers: getAuthHeader() });
      setStudentInfo(studentRes.data);
      
      // Use student-specific APIs that don't require admin permissions
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/attendance/my-attendance?month=${new Date().toISOString().slice(0, 7)}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/fee-management/my-fees`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/exams/my-exams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/timetable/my-timetable`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/notices`, { headers: getAuthHeader() })
      ]);

      // Process results - handle failures gracefully
      const [attendanceRes, feesRes, examsRes, timetableRes, noticesRes] = results;
      
      // Attendance
      if (attendanceRes.status === 'fulfilled') {
        const attData = attendanceRes.value.data;
        setAttendance(Array.isArray(attData) ? attData : attData?.records || []);
      }
      
      // Fees
      if (feesRes.status === 'fulfilled') {
        const feeData = feesRes.value.data;
        setFees(feeData?.invoices || []);
      }
      
      // Exams/Grades
      if (examsRes.status === 'fulfilled') {
        const examData = examsRes.value.data;
        setExams(examData?.exams || []);
        // Extract grades from exams - handle both new and legacy format
        const gradesFromExams = (examData?.exams || [])
          .filter(e => {
            // Check if exam has results in new format (subjects with marks)
            const hasNewResults = e.subjects && e.subjects.some(s => s.marks_obtained !== null);
            // Check legacy format
            const hasLegacyResults = e.results && e.results.length > 0;
            // Check published status
            const isPublished = e.results_published;
            return hasNewResults || hasLegacyResults || isPublished;
          })
          .map(e => ({
            exam_id: e.id,
            exam_name: e.name,
            marks_obtained: e.total_obtained || (e.results?.[0]?.marks_obtained) || 0,
            total_marks: e.total_max || e.max_marks || e.total_marks || 100,
            percentage: e.percentage || 0,
            subjects: e.subjects || e.results || []
          }));
        setGrades(gradesFromExams);
      }
      
      // Timetable
      if (timetableRes.status === 'fulfilled') {
        const ttData = timetableRes.value.data;
        // API returns {timetable: {monday: [], tuesday: [], ...}} format
        if (ttData?.timetable && typeof ttData.timetable === 'object') {
          // Flatten all days into a single array with day info
          const allEntries = [];
          Object.entries(ttData.timetable).forEach(([day, entries]) => {
            if (Array.isArray(entries)) {
              entries.forEach(entry => {
                allEntries.push({
                  ...entry,
                  day: day
                });
              });
            }
          });
          setTimetable(allEntries);
        } else if (Array.isArray(ttData?.timetable)) {
          setTimetable(ttData.timetable);
        } else if (Array.isArray(ttData)) {
          setTimetable(ttData);
        } else {
          setTimetable([]);
        }
      }
      
      // Notices
      if (noticesRes.status === 'fulfilled') {
        setNotices((noticesRes.value.data || []).slice(0, 5));
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Don't show error toast - partial data is ok
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const totalAttendance = attendance.length;
  const presentDays = attendance.filter(a => a.status === 'present').length;
  const attendanceRate = totalAttendance > 0 ? ((presentDays / totalAttendance) * 100).toFixed(1) : 0;

  // Calculate stats from fee invoices
  const feesSummary = fees.reduce((acc, f) => {
    // Use net_amount or amount for total, paid_amount for paid
    acc.total += f.net_amount || f.amount || 0;
    acc.paid += f.paid_amount || f.amount_paid || 0;
    return acc;
  }, { total: 0, paid: 0 });
  const totalFees = feesSummary.total;
  const paidFees = feesSummary.paid;
  // Ensure pending fees is never negative (can happen with overpayments or data inconsistency)
  const pendingFees = Math.max(0, totalFees - paidFees);

  const avgGrade = grades.length > 0 
    ? (grades.reduce((sum, g) => sum + ((g.marks_obtained / (g.total_marks || 100)) * 100), 0) / grades.length).toFixed(1)
    : 0;

  // Get today's classes
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todayClasses = timetable.filter(t => t.day?.toLowerCase() === today);

  const statCards = [
    { 
      title: 'Attendance Rate', 
      value: `${attendanceRate}%`, 
      icon: Calendar, 
      color: 'bg-green-500',
      subtext: `${presentDays}/${totalAttendance} days`
    },
    { 
      title: 'Pending Fees', 
      value: `₹${pendingFees.toLocaleString()}`, 
      icon: DollarSign, 
      color: pendingFees > 0 ? 'bg-red-500' : 'bg-green-500',
      subtext: pendingFees > 0 ? 'Payment due' : 'All paid'
    },
    { 
      title: 'Average Grade', 
      value: `${avgGrade}%`, 
      icon: TrendingUp, 
      color: 'bg-blue-500',
      subtext: `${grades.length} exams`
    },
    { 
      title: "Today's Classes", 
      value: todayClasses.length, 
      icon: BookOpen, 
      color: 'bg-purple-500',
      subtext: today.charAt(0).toUpperCase() + today.slice(1)
    }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Student Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Student Dashboard">
      <div className="space-y-6">
        {/* Welcome Banner */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Welcome, {studentInfo?.name || 'Student'}!</h2>
                  <p className="text-blue-100 mt-1">
                    {studentInfo?.class_name} {studentInfo?.section ? `- ${studentInfo.section}` : ''} | Roll No: {studentInfo?.roll_number || 'N/A'} | Session: {studentInfo?.session || '2025-26'}
                  </p>
                </div>
                <GraduationCap className="h-16 w-16 text-blue-200 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card data-testid={`stat-${stat.title.toLowerCase().replace(/[' ]/g, '-')}`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                    <div className={`${stat.color} p-2 rounded-lg`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Attendance & Fees */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="attendance">
              <TabsList>
                <TabsTrigger value="attendance">Attendance</TabsTrigger>
                <TabsTrigger value="fees">Fee Status</TabsTrigger>
                <TabsTrigger value="grades">Exam Results</TabsTrigger>
              </TabsList>

              <TabsContent value="attendance">
                <Card>
                  <CardHeader>
                    <CardTitle>Attendance Overview</CardTitle>
                    <CardDescription>Your attendance record this session</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Overall Attendance</span>
                        <span className="text-sm font-bold">{attendanceRate}%</span>
                      </div>
                      <Progress value={parseFloat(attendanceRate)} className="h-3" />
                      
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-green-600 mx-auto" />
                          <p className="text-2xl font-bold text-green-600 mt-2">{presentDays}</p>
                          <p className="text-xs text-muted-foreground">Present</p>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg">
                          <XCircle className="h-6 w-6 text-red-600 mx-auto" />
                          <p className="text-2xl font-bold text-red-600 mt-2">{totalAttendance - presentDays}</p>
                          <p className="text-xs text-muted-foreground">Absent</p>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <Calendar className="h-6 w-6 text-blue-600 mx-auto" />
                          <p className="text-2xl font-bold text-blue-600 mt-2">{totalAttendance}</p>
                          <p className="text-xs text-muted-foreground">Total Days</p>
                        </div>
                      </div>

                      {/* Recent Attendance */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium mb-2">Recent Attendance</h4>
                        <div className="flex gap-1 flex-wrap">
                          {attendance.slice(-20).map((a, i) => (
                            <div 
                              key={i}
                              className={`w-6 h-6 rounded-sm ${
                                a.status === 'present' ? 'bg-green-500' : 
                                a.status === 'absent' ? 'bg-red-500' : 'bg-amber-500'
                              }`}
                              title={`${a.date}: ${a.status}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="fees">
                <Card>
                  <CardHeader>
                    <CardTitle>Fee Status</CardTitle>
                    <CardDescription>Your fee payment summary</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground">Total Fees</p>
                          <p className="text-xl font-bold">₹{totalFees.toLocaleString()}</p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-600">Paid</p>
                          <p className="text-xl font-bold text-green-600">₹{paidFees.toLocaleString()}</p>
                        </div>
                        <div className="text-center p-4 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-600">Pending</p>
                          <p className="text-xl font-bold text-red-600">₹{pendingFees.toLocaleString()}</p>
                        </div>
                      </div>

                      {fees.length > 0 ? (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium mb-2">Fee Details</h4>
                          <div className="space-y-2">
                            {fees.map(fee => (
                              <div key={fee.id} className="flex items-center justify-between p-3 border rounded-lg">
                                <div>
                                  <p className="font-medium">{fee.fee_head_name || fee.description || 'Fee'}</p>
                                  <p className="text-xs text-muted-foreground">Due: {fee.due_date}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold">₹{fee.amount}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    fee.status === 'paid' ? 'bg-green-100 text-green-800' :
                                    fee.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {fee.status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-4">No fee records found</p>
                      )}

                      {pendingFees > 0 && (
                        <Button className="w-full mt-4" onClick={() => window.location.href = '/fees'}>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pay Now
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="grades">
                <Card>
                  <CardHeader>
                    <CardTitle>Exam Results</CardTitle>
                    <CardDescription>Your performance in examinations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {grades.length > 0 ? (
                      <div className="space-y-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-600">Average Score</p>
                          <p className="text-3xl font-bold text-blue-600">{avgGrade}%</p>
                        </div>

                        <div className="space-y-3">
                          {grades.map((grade, idx) => {
                            const percentage = grade.percentage || 
                              (grade.total_marks > 0 ? ((grade.marks_obtained / grade.total_marks) * 100).toFixed(1) : 0);
                            return (
                              <div key={idx} className="p-3 border rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <p className="font-medium">{grade.exam_name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {grade.subjects?.length || 0} subjects
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-lg">{grade.marks_obtained}/{grade.total_marks}</p>
                                    <p className={`text-sm font-medium ${
                                      percentage >= 60 ? 'text-green-600' : 
                                      percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>{percentage}%</p>
                                  </div>
                                </div>
                                <Progress value={parseFloat(percentage)} className="h-2" />
                                
                                {/* Subject breakdown */}
                                {grade.subjects && grade.subjects.length > 0 && (
                                  <div className="mt-2 pt-2 border-t">
                                    <div className="flex flex-wrap gap-2">
                                      {grade.subjects.slice(0, 4).map((s, i) => (
                                        <span key={i} className="text-xs bg-muted px-2 py-1 rounded">
                                          {s.subject_name || s.subject}: {s.marks_obtained ?? s.marks}/{s.max_marks || 100}
                                        </span>
                                      ))}
                                      {grade.subjects.length > 4 && (
                                        <span className="text-xs text-muted-foreground">
                                          +{grade.subjects.length - 4} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No exam results available yet</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Schedule & Notices */}
          <div className="space-y-6">
            {/* Today's Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Today's Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayClasses.length > 0 ? (
                  <div className="space-y-3">
                    {todayClasses.sort((a, b) => a.time_slot?.localeCompare(b.time_slot)).map((cls, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                        <div className="w-16 text-xs font-mono text-muted-foreground">
                          {cls.time_slot?.split('-')[0]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{cls.subject}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No classes today</p>
                )}
              </CardContent>
            </Card>

            {/* Notices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Recent Notices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notices.length > 0 ? (
                  <div className="space-y-3">
                    {notices.map(notice => (
                      <div key={notice.id} className="p-3 border rounded-lg">
                        <p className="font-medium text-sm">{notice.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notice.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No notices</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
